package fr.frux.feature.profile

import fr.frux.core.common.logging.Logger
import fr.frux.core.common.prefs.AppPreferences
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.shared.domain.model.UserProfile
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours
import kotlin.time.Instant

/** Nombre d'expositions du rappel avant qu'il ne se taise pour de bon. */
internal const val NUDGE_MAX_EXPOSURES = 3

/** Deux ouvertures du profil dans cet intervalle ne comptent que pour une exposition. */
internal val NUDGE_EXPOSURE_SPACING: Duration = 24.hours

/** Silence après une fermeture, puis les expositions restantes reprennent. */
internal val NUDGE_DISMISS_SILENCE: Duration = 7.days

internal const val NUDGE_FIRST_TREE_KEY = "profile_nudge_first_tree"
internal const val NUDGE_ENRICH_KEY = "profile_nudge_enrich"

/** Carte d'accompagnement du profil : créer un premier arbre, puis enrichir ceux qu'on a. */
enum class ContributionNudge { FIRST_TREE, ENRICH }

/**
 * La vérité « a déjà contribué » vient du serveur (`treesCount`), pas de l'appareil : un compte
 * ancien sur un appareil neuf reçoit l'enrichissement, jamais le rappel du premier arbre. Sans
 * profil relu (hors ligne, pas encore chargé), rien n'est montré.
 */
internal fun nudgeFor(contributions: UserProfile?): ContributionNudge? =
    when {
        contributions == null -> null
        contributions.treesCount == 0 -> ContributionNudge.FIRST_TREE
        else -> ContributionNudge.ENRICH
    }

/** Ce que l'appareil retient d'une variante : expositions comptées et dernière fermeture. */
internal data class NudgeMemory(
    val exposures: Int = 0,
    val lastExposureAt: Instant? = null,
    val dismissedAt: Instant? = null,
)

internal sealed interface NudgeDecision {
    data object Hidden : NudgeDecision

    data class Show(
        val countsAsExposure: Boolean,
    ) : NudgeDecision
}

/**
 * Une horloge reculée (date mémorisée dans le futur) ne tait rien et ne bloque pas le comptage :
 * mieux vaut un rappel de trop qu'un silence sans fin, comme `isEmptyAreaSilenced`.
 */
internal fun nudgeDecision(
    memory: NudgeMemory,
    now: Instant,
): NudgeDecision {
    val sinceDismissal = memory.dismissedAt?.let { now - it }
    val silenced = sinceDismissal != null && sinceDismissal >= Duration.ZERO && sinceDismissal < NUDGE_DISMISS_SILENCE
    if (silenced || memory.exposures >= NUDGE_MAX_EXPOSURES) return NudgeDecision.Hidden
    val sinceExposure = memory.lastExposureAt?.let { now - it }
    val isNewExposure =
        sinceExposure == null || sinceExposure < Duration.ZERO || sinceExposure >= NUDGE_EXPOSURE_SPACING
    return NudgeDecision.Show(countsAsExposure = isNewExposure)
}

/**
 * Mémoire du rappel entre deux lancements. Même discipline que `EmptyAreaDismissStore` : toute
 * erreur de stockage est avalée, une lecture ratée ou corrompue vaut une mémoire vierge.
 */
class ContributionNudgeStore(
    private val preferences: AppPreferences,
) {
    internal suspend fun load(nudge: ContributionNudge): NudgeMemory =
        runCatchingCancellable { preferences.getString(keyOf(nudge)) }
            .onFailure { Logger.e(TAG, "Lecture du rappel de contribution impossible", it) }
            .getOrNull()
            ?.let(::parse)
            ?: NudgeMemory()

    internal suspend fun save(
        nudge: ContributionNudge,
        memory: NudgeMemory,
    ) {
        val raw =
            listOf(
                memory.exposures.toString(),
                memory.lastExposureAt
                    ?.toEpochMilliseconds()
                    ?.toString()
                    .orEmpty(),
                memory.dismissedAt
                    ?.toEpochMilliseconds()
                    ?.toString()
                    .orEmpty(),
            ).joinToString(SEPARATOR)
        runCatchingCancellable { preferences.putString(keyOf(nudge), raw) }
            .onFailure { Logger.e(TAG, "Écriture du rappel de contribution impossible", it) }
    }

    suspend fun clearAll() {
        ContributionNudge.entries.forEach { nudge ->
            runCatchingCancellable { preferences.remove(keyOf(nudge)) }
                .onFailure { Logger.e(TAG, "Purge du rappel de contribution impossible", it) }
        }
    }

    private fun keyOf(nudge: ContributionNudge): String =
        when (nudge) {
            ContributionNudge.FIRST_TREE -> NUDGE_FIRST_TREE_KEY
            ContributionNudge.ENRICH -> NUDGE_ENRICH_KEY
        }

    private fun parse(raw: String): NudgeMemory? {
        val parts = raw.split(SEPARATOR)
        val exposures = parts.getOrNull(0)?.toIntOrNull()?.takeIf { it >= 0 }
        val last = parseInstant(parts.getOrNull(1))
        val dismissed = parseInstant(parts.getOrNull(2))
        val wellFormed = parts.size == FIELD_COUNT && last.isSuccess && dismissed.isSuccess
        return if (wellFormed && exposures != null) {
            NudgeMemory(exposures, last.getOrNull(), dismissed.getOrNull())
        } else {
            null
        }
    }

    /** Champ vide = `null` voulu (succès), champ illisible = valeur corrompue (échec). */
    private fun parseInstant(raw: String?): Result<Instant?> =
        when {
            raw == null -> {
                Result.failure(IllegalArgumentException("champ absent"))
            }

            raw.isEmpty() -> {
                Result.success(null)
            }

            else -> {
                raw.toLongOrNull()?.let { Result.success(Instant.fromEpochMilliseconds(it)) }
                    ?: Result.failure(IllegalArgumentException("champ illisible"))
            }
        }

    private companion object {
        const val TAG = "ContributionNudgeStore"
        const val SEPARATOR = ";"
        const val FIELD_COUNT = 3
    }
}

/**
 * Logique du rappel sortie de [ProfileViewModel] (ratchet detekt sur le constructeur et la
 * taille). Tient la mémoire de chaque variante en cache pour ne pas recompter une exposition dans
 * la même instance : rouvrir le profil dix fois le même jour vaut une exposition.
 */
internal class ContributionNudgeController(
    private val store: ContributionNudgeStore,
    private val scope: CoroutineScope,
    private val now: () -> Instant,
    private val publish: (ContributionNudge?) -> Unit,
) {
    private val cache = mutableMapOf<ContributionNudge, NudgeMemory>()
    private val lock = Mutex()
    private var current: ContributionNudge? = null

    suspend fun onContributions(profile: UserProfile?) {
        lock.withLock {
            val nudge = nudgeFor(profile)
            current = null
            if (nudge == null) {
                publish(null)
                return
            }
            val memory = cache[nudge] ?: store.load(nudge)
            val moment = now()
            when (val decision = nudgeDecision(memory, moment)) {
                NudgeDecision.Hidden -> {
                    cache[nudge] = memory
                    publish(null)
                }

                is NudgeDecision.Show -> {
                    current = nudge
                    val updated =
                        if (decision.countsAsExposure) {
                            memory.copy(exposures = memory.exposures + 1, lastExposureAt = moment)
                        } else {
                            memory
                        }
                    cache[nudge] = updated
                    publish(nudge)
                    if (decision.countsAsExposure) store.save(nudge, updated)
                }
            }
        }
    }

    fun dismiss() {
        val nudge = current ?: return
        current = null
        publish(null)
        val dismissed = (cache[nudge] ?: NudgeMemory()).copy(dismissedAt = now())
        cache[nudge] = dismissed
        scope.launch { store.save(nudge, dismissed) }
    }

    /** Déconnexion ou compte effacé : l'appareil partagé repart de zéro pour le compte suivant. */
    suspend fun forget() {
        lock.withLock {
            current = null
            cache.clear()
            publish(null)
            store.clearAll()
        }
    }
}

/**
 * Réglages du [ProfileViewModel] regroupés pour tenir sous le plafond detekt de paramètres de
 * constructeur. [nudgeStore] à `null` coupe le rappel (tests qui ne l'exercent pas).
 */
data class ProfileOptions(
    val donationsAllowed: Boolean,
    val nudgeStore: ContributionNudgeStore? = null,
    val now: () -> Instant = { Clock.System.now() },
)
